<template>
  <div class="guides-page">
    <HeadNav selectedLanguage="en" page="guides" />

    <header class="guides-hero">
      <div class="hero-glow" aria-hidden="true" />
      <div class="container hero-inner">
        <h1>Secure VPN Guides</h1>
        <p class="intro">
          Welcome to the Secure VPN Guides Hub. Here you'll find step-by-step tutorials, troubleshooting articles, security tips, and practical advice to help you get the most out of Secure VPN on Android, Windows, Mac, iPhone, and other devices.
        </p>
      </div>
    </header>

    <main class="container main-content">
      <section class="latest-guides">
        <div class="section-head">
          <h2>Latest Guides</h2>
          <p>Practical tips and clear answers, updated for everyday Secure VPN use.</p>
        </div>

        <div class="guide-list">
          <article v-for="guide in sortedGuides" :key="guide.slug" class="guide-item">
            <router-link :to="guide.path" class="guide-link">
              <div class="guide-media">
                <img :src="guide.poster" :alt="guide.h1" loading="lazy">
              </div>
              <div class="guide-copy">
                <div class="guide-meta">
                  <time :datetime="guide.date">{{ formatDate(guide.date) }}</time>
                  <span class="meta-dot" aria-hidden="true" />
                  <span>Guide</span>
                </div>
                <h3>{{ guide.title }}</h3>
                <p>{{ guide.description }}</p>
                <span class="read-more">
                  Read guide
                  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                    <path d="M4 10h12M11 5l5 5-5 5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </span>
              </div>
            </router-link>
          </article>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import '@/css/base.scss';
import '@/css/vpn-theme.scss';
import HeadNav from '@/components/HeadNav.vue';
import guides from '@/documents/guides';

export default {
  name: 'GuidesPage',
  components: {
    HeadNav,
  },
  computed: {
    sortedGuides() {
      return [...guides].sort((a, b) => new Date(b.date) - new Date(a.date));
    },
  },
  methods: {
    formatDate(date) {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.guides-page {
  min-height: 70vh;
  background:
    radial-gradient(ellipse 80% 50% at 10% -10%, rgba(91, 79, 232, 0.12), transparent 55%),
    radial-gradient(ellipse 60% 40% at 90% 0%, rgba(59, 105, 255, 0.08), transparent 50%),
    #f5f4fb;
  color: var(--vpn-ink);
  font-family: var(--vpn-font);
}

.container {
  width: var(--vpn-container);
  max-width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
}

.guides-hero {
  position: relative;
  overflow: hidden;
  padding: 64px 0 52px;
  border-bottom: 1px solid rgba(91, 79, 232, 0.08);
}

.hero-glow {
  position: absolute;
  inset: auto 50% -40% auto;
  width: 520px;
  height: 520px;
  transform: translateX(20%);
  background: radial-gradient(circle, rgba(139, 124, 255, 0.22), transparent 68%);
  pointer-events: none;
}

.hero-inner {
  position: relative;
  z-index: 1;
  margin: 0 auto;
  text-align: left;
  animation: vpn-rise 0.65s ease both;
}

h1 {
  margin: 0 0 18px;
  font-size: clamp(34px, 5vw, 48px);
  line-height: 1.12;
  letter-spacing: -0.03em;
  font-weight: 800;
}

.intro {
  margin: 0;
  font-size: 17px;
  line-height: 1.75;
  color: var(--vpn-ink-soft);
}

.main-content {
  padding: 48px 0 88px;
}

.section-head {
  margin-bottom: 28px;
  animation: vpn-fade 0.7s ease both;
}

.section-head h2 {
  margin: 0 0 8px;
  font-size: clamp(24px, 3vw, 30px);
  font-weight: 800;
  letter-spacing: -0.02em;
}

.section-head p {
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
  color: var(--vpn-muted);
}

.guide-list {
  display: grid;
  gap: 22px;
}

.guide-item {
  min-width: 0;
  animation: vpn-rise 0.7s ease both;
  animation-delay: 0.08s;
}

/* 用 flex 固定左图右文，避免 grid + 绝对定位在全局 img 样式下错位 */
.guides-page a.guide-link {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  background: #fff;
  border: 1px solid var(--vpn-line);
  border-radius: 22px;
  box-shadow: var(--vpn-shadow-soft);
  text-decoration: none;
  color: inherit;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
}

.guides-page a.guide-link:hover {
  transform: translateY(-3px);
  border-color: rgba(91, 79, 232, 0.22);
  box-shadow: 0 22px 48px rgba(67, 56, 202, 0.14);
}

.guides-page .guide-media {
  flex: 0 0 320px;
  width: 320px;
  max-width: 38%;
  position: relative;
  min-height: 220px;
  overflow: hidden;
  background: linear-gradient(135deg, #12122e, #2a2a6a);
}

.guides-page .guide-media img {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  max-width: none;
  object-fit: cover;
  object-position: center top;
  transition: transform 0.45s ease;
}

.guides-page a.guide-link:hover .guide-media img {
  transform: scale(1.04);
}

.guides-page .guide-copy {
  display: flex;
  flex: 1 1 0;
  min-width: 0;
  flex-direction: column;
  justify-content: center;
  padding: 28px 32px;
}

.guide-meta {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  font-size: 13px;
  font-weight: 700;
  color: var(--vpn-teal);
}

.meta-dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: rgba(91, 79, 232, 0.4);
}

.guide-copy h3 {
  margin: 0 0 12px;
  font-size: clamp(20px, 2.4vw, 24px);
  line-height: 1.35;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--vpn-ink);
}

.guide-copy p {
  margin: 0 0 22px;
  font-size: 15px;
  line-height: 1.7;
  color: var(--vpn-ink-soft);
}

.read-more {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 800;
  color: var(--vpn-teal-deep);
}

.read-more svg {
  width: 16px;
  height: 16px;
  transition: transform 0.2s ease;
}

.guides-page a.guide-link:hover .read-more svg {
  transform: translateX(3px);
}

@media (max-width: 860px) {
  .guides-hero {
    padding: 48px 0 40px;
  }

  .guides-page a.guide-link {
    flex-direction: column;
  }

  .guides-page .guide-media {
    flex: none;
    width: 100%;
    max-width: none;
  }

  .guides-page .guide-media {
    min-height: 0;
    aspect-ratio: 16 / 9;
  }

  .guides-page .guide-media img {
    position: absolute;
    inset: 0;
    height: 100%;
  }

  .guides-page .guide-copy {
    padding: 22px 20px 24px;
  }
}

@media (max-width: 720px) {
  .main-content {
    padding: 36px 0 64px;
  }

  .intro {
    font-size: 16px;
  }
}
</style>
